import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { Match } from 'navigo';
import { IRouteStore } from 'stores/RouteStore';
import { serverUri } from 'utils/config';

export class ConfirmEmailStore {
  status:
    | 'none'
    | 'resendEmailLoading'
    | 'resendEmailSuccess'
    | 'resendEmailError'
    | 'checkTokenLoading'
    | 'checkTokenError'
    | 'checkTokenSuccess' = 'none';
  message?: string;
  email?: string;

  constructor(private readonly authStore: AuthStore, private readonly routeStore: IRouteStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get currentUserEmail() {
    return this.authStore.currentUser?.email;
  }

  async activateEmailSent(match: Match) {
    this.status = 'resendEmailSuccess';
    this.message = 'An email was sent to your address. Please click the link in the email to confirm your address.';
  }

  async activateCheckToken(match: Match) {
    const token = match?.data?.token;
    await this.checkToken(token || '');
  }

  async resendEmail(email?: string) {
    this.status = 'resendEmailLoading';
    this.message = undefined;
    if (!!email) {
      this.email = email;
    }
    try {
      if (!this.email) {
        runInAction(() => {
          this.status = 'resendEmailError';
          this.message = 'Unable to send email confirmation. Please try again.';
        });
      }

      const response = await fetch(`${serverUri}/user/email/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: this.email }),
      });

      if (!response.ok) throw new Error('Unable to send email confirmation.');

      runInAction(() => {
        this.status = 'resendEmailSuccess';
        this.message = 'An email was sent to your address. Please click the link in the email to confirm your address.';
      });
      this.routeStore.navigateByName('confirmEmailSent');
    } catch (err) {
      runInAction(() => {
        this.status = 'resendEmailError';
        this.message = 'Unable to send email confirmation. Please try again.';
      });
    }
  }

  async checkToken(token: string) {
    this.status = 'checkTokenLoading';
    this.message = undefined;
    try {
      const response = await fetch(`${serverUri}/user/email/confirm/${encodeURIComponent(token)}`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Unable to confirm email address.');
      if (this.authStore.isLoggedIn) {
        this.authStore.setEmailConfirmed(true);
      }
      runInAction(() => {
        this.status = 'checkTokenSuccess';
        this.message = 'Your email address was confirmed.';
      });
    } catch (err) {
      runInAction(() => {
        this.status = 'checkTokenError';
        this.message = 'Unable to confirm email address. The link may have expired. Please try again.';
      });
    }
  }
}
